import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in with your new password");
      navigate("/login");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6">
      <div className="max-w-sm w-full">
        <p className="text-xs tracking-[0.25em] uppercase text-accent font-semibold mb-3">Unified Transaction Hub</p>
        <h1 className="text-3xl text-foreground leading-tight mb-2">Set a new password</h1>
        {!token ? (
          <p className="text-sm text-accent mt-4" data-testid="reset-missing-token">This reset link is missing its token. Please use the link from your email or request a new one.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-6" data-testid="reset-form">
            <div>
              <Label htmlFor="password" className="text-xs tracking-widest uppercase text-muted-foreground">New password</Label>
              <Input id="password" data-testid="reset-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-card" placeholder="••••••••" />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs tracking-widest uppercase text-muted-foreground">Confirm password</Label>
              <Input id="confirm" data-testid="reset-confirm-input" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 bg-card" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="reset-submit-button"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full gap-2">
              <KeyRound className="w-4 h-4" />
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
        <button data-testid="reset-back-to-login" onClick={() => navigate("/login")} className="text-sm text-primary font-semibold underline underline-offset-4 mt-6">
          Back to sign in
        </button>
      </div>
    </div>
  );
}
